import {RequestInfo} from "@khan/shared-core";

/**
 * Based on the locale, return the language segmemt that is going to be used by
 * Zendesk.
 *
 * This does not detect all KA languages, only our "priority" languages. These
 * are the ones that we prioritize translations to and want to ensure a
 * consistent experience from KA to Zendesk.
 *
 * @param {boolean} useNonSpecifiedLocale If set, use `/en-us` as the default
 * locale. Otherwise, let Zendesk resolve where to redirect (`/`). Defaults to true.
 * @returns {string} The string representing the locale segment.
 */
const getZendeskLangSegment = (useNonSpecifiedLocale = true) => {
    // Note: Not all languages are detected here, these were indicated to us by
    // Support. Slack conversation here:
    // https://khanacademy.slack.com/archives/CLYV23MNF/p1579200096016400?thread_ts=1579198490.015500&cid=CLYV23MNF.
    // From Laurie on 1/16/20:
    // > there’s a (kind of high) chance that Spanish + Portuguese won’t get
    // > correctly routed if we only rely on Zendesk’s logic, so for those two
    // > languages at least it is better to force it if possible. the rest of
    // > our languages can safely be the non-specified link because a) English
    // > is the default and b) there’s no short term plans to update Zendesk’s
    // > language support to more than what we currently have.
    //
    // Update: The non-specified link breaks on certain pages (e.g.
    // community), so we will need to specify when not to use the fallback
    // version and use the english one instead.
    if (useNonSpecifiedLocale && RequestInfo.kaLocale === "en") {
        return "/en-us";
    }
    if (RequestInfo.kaLocale === "es") {
        return "/es";
    }
    if (RequestInfo.kaLocale === "pt") {
        return "/pt-br";
    }
    return useNonSpecifiedLocale ? "" : "/en-us";
};

/**
 * Get a Zendesk URL in the best matching language that points to the home
 * page.
 */
export const getZendeskLink = (): string => {
    const zendeskLangSegment = getZendeskLangSegment(true);

    return `https://khanacademy.zendesk.com/hc${zendeskLangSegment}`;
};

/**
 * Get a Zendesk URL in the best matching language for the given article ID.
 */
export const getZendeskArticleLink = (
    id: string,
    useNonSpecifiedLocale = true,
): string => {
    const zendeskLangSegment = getZendeskLangSegment(useNonSpecifiedLocale);

    return `https://khanacademy.zendesk.com/hc${zendeskLangSegment}/articles/${id}`;
};

/**
 * Get a Zendesk URL in the best matching language that points to the community
 * page.
 */
export const getZendeskCommunityLink = (): string => {
    const zendeskLangSegment = getZendeskLangSegment(false);

    return `https://khanacademy.zendesk.com/hc${zendeskLangSegment}/community/topics`;
};
